import { computed, makeObservable } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { PublicUser } from '../models/PublicUser'
import type { RoomState } from '../models/RoomState'
import type { RoomStateAnswer } from '../models/RoomStateAnswer'
import type { Section } from '../models/Section'
import type { SlideQuestion } from '../models/SlideQuestion'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { ObservableMapStore } from '../stores/ObservableMapStore'
import { SimpleObservableValue } from '../stores/SimpleObservableValue'
import { Cubit } from './core'

export type DecoratedAnswer = {
  user: PublicUser
  answer: RoomStateAnswer
  groupName: string
}

type AnswerBreakdown = {
  answerIndex: number
  answer: string
  ratio: number
  ratioAsPercentage: string
  numberOfResponses: number
  isCorrect: boolean
}

export type ResultField = 'name' | 'answer' | 'group'

export class QuizPollResultsCubit extends Cubit {
  repository: FirebaseRepository

  section: Section
  question: SlideQuestion
  answers: RoomStateAnswer[]
  roomStates: RoomState[]

  filters = new ObservableMapStore<{
    [K in ResultField]?: string
  }>()

  sorting = new SimpleObservableValue<{
    field: ResultField
    direction: 'asc' | 'desc'
  }>({
    field: 'name',
    direction: 'asc',
  })

  constructor(
    repository: FirebaseRepository,
    params: {
      section: Section
      question: SlideQuestion
      answers: RoomStateAnswer[]
      roomStates: RoomState[]
    }
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.section = params.section
    this.question = params.question
    this.answers = params.answers
    this.roomStates = params.roomStates
  }

  dispose() {
    return super.dispose()
  }

  @computed
  get decoratedAnswers(): DecoratedAnswer[] {
    const decoratedAnswers: DecoratedAnswer[] = []
    for (const a of this.answers) {
      const user = this.repository.userStore.getUser(a.data.userId)
      const roomState = this.roomStates.find((r) => r.id === a.data.roomId)
      // Ignore an answer if the user or roomState is not found; it feels like
      // this should be an invariant of some kind prevented at the data layer
      if (!user || !roomState) continue

      decoratedAnswers.push({
        user,
        answer: a,
        groupName: this.roomStateName(roomState),
      })
    }
    return decoratedAnswers
  }

  @computed
  get filteredAnswers(): DecoratedAnswer[] {
    if (!this.filters.size) return this.decoratedAnswers

    const utils = this.repository.utils

    let answers = this.decoratedAnswers

    for (const field of this.filters.keys) {
      const term = this.filters.get(field)

      // if term is empty string or undefined, skip filtering by this field
      if (!term) continue

      answers = answers.filter((item) => {
        switch (field) {
          case 'name':
            return utils.searchString(item.user.fullName, term)
          case 'group':
            return utils.searchString(item.groupName, term)
          case 'answer':
            return utils.searchString(item.answer.data.answer.toString(), term)
          default: {
            // we have a lint rule for exhaustive switches, but it does not work here?
            const exhaustiveCheck: never = field
            return exhaustiveCheck
          }
        }
      })
    }

    return answers
  }

  @computed
  get filteredSortedAnswers(): DecoratedAnswer[] {
    if (!this.sorting.value) return this.filteredAnswers

    const sortStrategy = this.sorting.value.field
    const sortDirection = this.sorting.value.direction
    const directionMod = sortDirection === 'asc' ? 1 : -1

    return this.filteredAnswers.slice().sort((a, b) => {
      switch (sortStrategy) {
        case 'name':
          return (
            this.repository.utils.compareStrings(
              a.user.fullName,
              b.user.fullName
            ) * directionMod
          )
        case 'group': {
          return (
            this.repository.utils.compareStrings(a.groupName, b.groupName) *
            directionMod
          )
        }
        case 'answer':
          return (
            this.repository.utils.compareStrings(
              a.answer.data.answer.toString(),
              b.answer.data.answer.toString()
            ) * directionMod
          )
      }
    })
  }

  @computed
  get breakdown(): AnswerBreakdown[] {
    const filteredSortedAnswers = this.filteredSortedAnswers
    if (this.question.data.questionType === SlideQuestionType.poll) {
      const yes = filteredSortedAnswers.filter(
        (answer) => answer.answer.data.answer === 1
      ).length
      const no = filteredSortedAnswers.length - yes
      const yesPercentage = filteredSortedAnswers.length
        ? yes / filteredSortedAnswers.length
        : 0
      const noPercentage = filteredSortedAnswers.length ? 1 - yesPercentage : 0
      return [
        {
          answerIndex: 0,
          answer: 'Yes',
          ratio: yesPercentage,
          ratioAsPercentage: `${Math.round(yesPercentage * 100)}%`,
          numberOfResponses: yes,
          isCorrect: false,
        },
        {
          answerIndex: 1,
          answer: 'No',
          ratio: noPercentage,
          ratioAsPercentage: `${Math.round(noPercentage * 100)}%`,
          numberOfResponses: no,
          isCorrect: false,
        },
      ]
    }
    return this.question.data.answers.map((answer, index) => {
      const matchingAnswers = filteredSortedAnswers.filter(
        (a) => a.answer.data.answer === index
      )
      const ratio = filteredSortedAnswers.length
        ? matchingAnswers.length / filteredSortedAnswers.length
        : 0
      return {
        answerIndex: index,
        answer: answer,
        ratio,
        ratioAsPercentage: `${Math.round(ratio * 100)}%`,
        numberOfResponses: matchingAnswers.length,
        isCorrect:
          this.question.data.questionType ===
            SlideQuestionType.multipleChoice &&
          this.question.data.correct === index,
      }
    })
  }

  @computed
  get uniqueGroupNames(): string[] {
    return Array.from(new Set(this.decoratedAnswers.map((a) => a.groupName)))
  }

  private roomStateName = (roomState: RoomState) => {
    let groupName = roomState.roomStateName
    if (!groupName || groupName === '') {
      if (roomState.groupLeaderUserIds.length > 0) {
        const groupLeaderId = roomState.groupLeaderUserIds[0]
        const groupLeader = this.repository.userStore.getUser(groupLeaderId)
        if (groupLeader) {
          groupName = groupLeader.fullName
        } else if (
          roomState.groupLeaderUserIds.includes(this.section.instructor.id)
        ) {
          groupName = this.section.instructor.fullName
        }
      }
    }
    return groupName || 'Group has no name'
  }
}
